import React, { useEffect, useMemo, useRef, useState } from 'react';
import classNames from 'classnames';
import { debounce } from 'underscore';
import { v4 as uuid } from 'uuid';

import { BlProfile } from '../../../types/working-model';
import { mergeRefs } from '../../../utils/merge-refs';
import { Field, FieldLabel, FieldProps } from '../field/field';
import Icon, { COLORS } from '../icon';

export interface TextInputProps<T = string | number> extends FieldProps<T> {
	onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
	onBlur?: (e: React.FocusEvent<HTMLInputElement>) => void;
	onFocus?: (e: React.FocusEvent<HTMLInputElement>) => void;
	value?: string;
	style?: { [key: string]: any };
	numeric?: boolean;
	email?: boolean;
	disabled?: boolean;
	id?: string;
	password?: boolean;
	size?: 'large' | 'small' | 'normal';
	icon?: string;
	prefix?: string;
	onEscape?: () => void;
	onTab?: () => void;
	updateValue?: string; //deprecated and should not be used moving forward
	shouldUpdateValue?: boolean; //deprecated and should not be used moving forward
	errorMessage?: string;
	useFocusClick?: boolean; //use this if your input is inside a dragable component
	minLength?: number;
	maxLength?: number;
	min?: number;
	max?: number;
	step?: number;
	tooltip?: string | [string | (() => JSX.Element), string];
	profile?: BlProfile | null;
	channel?: number;
	persistent?: boolean;
	onKeyUp?: (e: React.KeyboardEvent<HTMLInputElement>) => void;
	autoFocus?: boolean;
	autoComplete?: string;
	className?: string;
	monospace?: boolean;
	noPlaceholder?: boolean;
	onDebounce?: (value: string) => void;
	debounceTime?: number;
}

export enum Validation {
	normal = 'normal',
	ok = 'ok',
	warn = 'warn',
	error = 'error',
}

// eslint-disable-next-line react/display-name
const TextInput: React.FC<TextInputProps> = React.forwardRef<HTMLInputElement, TextInputProps>((props, ref): JSX.Element => {
	const {
		tooltip,
		label,
		onChange,
		placeholder,
		defaultValue,
		style,
		numeric = false,
		email = false,
		disabled = false,
		password = false,
		id = uuid(),
		valid = Validation.normal,
		onFocus = () => ({}),
		onBlur = () => ({}),
		size = 'normal',
		icon,
		value,
		prefix,
		onEscape = () => ({}),
		onTab = () => ({}),
		updateValue = '',
		shouldUpdateValue = false,
		required,
		useFocusClick = false,
		name,
		min = 0,
		max = 999999999,
		step = 1,
		onKeyUp,
		autoFocus,
		autoComplete,
		className,
		monospace = false,
		noPlaceholder = false,
		onDebounce,
		debounceTime = 300
	} = props;
	const [entered, setEntered] = useState(false);
	const prefixRef = useRef<HTMLSpanElement | null>(null);
	const input = useRef<HTMLInputElement | null>(null);
	const [paddingLeft, setPaddingLeft] = useState(16);
	const [internalText, setInternalText] = useState(defaultValue || '');
	const fontFamily = monospace ? 'monospace' : 'inherit';

	const useInternalState = !value && value !== "";

	const debounced = useRef(debounce((value: string) => {
		onDebounce?.(value);
	}, debounceTime)).current;

	function handleChange(e: React.ChangeEvent<HTMLInputElement>) {
		if (useInternalState) {
			onChange?.(e);
			setInternalText(e.target.value);
		} else {
			onChange?.(e);
		}
		if (onDebounce) {
			debounced(e.target.value);
		}
	}

	function keyUp(e: React.KeyboardEvent<HTMLInputElement>) {
		setEntered(!!input.current?.value);
		if (onKeyUp) onKeyUp(e);
	}

	useEffect(() => {
		if (defaultValue) {
			setEntered(true);
		}
	}, [defaultValue]);

	useEffect(() => {
		if (autoFocus) {
			input.current?.focus();
		}
	}, [autoFocus]);

	//This way of updating the component is deprecated and should not be used moving forward
	useEffect(() => {
		if (input?.current?.value && shouldUpdateValue && value === undefined) {
			input.current.value = updateValue || '';
		}
	}, [updateValue, shouldUpdateValue, value]);

	useEffect(() => {
		if (prefixRef.current) {
			setTimeout(() => {
				const size = prefixRef.current?.getBoundingClientRect();
				setPaddingLeft(15 + (size?.width ?? 0));
			}, 150);
		}
	}, []);

	useEffect(() => {
		const handleKeyDown = (e: KeyboardEvent) => {
			if (input.current && document.activeElement === input.current) {
				if (e.key === 'Escape') {
					onEscape();
				}
				if (e.key === 'Tab') {
					onTab();
				}
			}
		};
		document.addEventListener('keydown', handleKeyDown);
		return () => {
			document.removeEventListener('keydown', handleKeyDown);
		};
	}, [input, onEscape, onTab]);

	const type = useMemo(() => {
		if (numeric) {
			return 'number';
		}
		if (email) {
			return 'email';
		}
		if (password) {
			return 'password';
		}
		return 'text';
	}, [email, numeric, password]);

	function handleFocusClick() {
		const isFocused = document.activeElement === input.current;
		if (useFocusClick && !isFocused) {
			input.current?.focus();
		}
	}

	const providedValue = value !== undefined ? value : defaultValue;
	const inputValue = useInternalState ? internalText : providedValue;

	return (
		<Field {...props}>
			<div
				className={classNames('field-group text', { entered }, valid, size, className)}
				style={style}
			>
				{label ? (
					<div style={{
						display: 'flex',
						alignItems: 'center'
					}}>
						<FieldLabel label={label} required={required} tooltip={tooltip} />
					</div>
				) : null}
				{prefix && (
					<span className="prefix" ref={prefixRef}>
						{prefix}
					</span>
				)}
				<input
					onClick={handleFocusClick}
					className={classNames('evt-field-input', { 'has-icon': !!icon })}
					onBlur={onBlur}
					onFocus={onFocus}
					id={name ? `file-input-${name}` : id}
					disabled={disabled}
					ref={mergeRefs(input, ref)}
					type={type}
					placeholder={noPlaceholder ? '' : (placeholder || label)}
					onChange={handleChange}
					onKeyUp={keyUp}
					value={inputValue}
					name={name}
					min={numeric ? min : undefined}
					max={numeric ? max : undefined}
					step={numeric ? step : undefined}
					style={{ paddingLeft, fontFamily }} //don't take this out - removing it caused a regression. If you need to make a change here, change the paddingLeft variable, but leave this in here.
					autoComplete={autoComplete || 'on'}
				/>

				{icon && (
					<div className="input-icon">
						<Icon name={icon} size={14} color={COLORS.DEFAULT_GRAY} />
					</div>
				)}
			</div>
		</Field>
	);
});
export default TextInput;
